import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { MapPin, Phone, User, Globe } from 'lucide-react';
import { brandConfig } from '../../../config/brandConfig';
import { useBranches } from '../../../hooks/useBranches';
import styles from './TrustSection.module.css';
import geoData from '../../../assets/data/india-geojson.json';

// Soft pastel palette for states
const mapColors = [
  '#FCE4EC', '#E8EAF6', '#E3F2FD', '#E0F2F1', 
  '#F1F8E9', '#FFF8E1', '#FFF3E0', '#FBE9E7', 
  '#F3E5F5', '#E1F5FE', '#E8F5E9', '#FFFDE7'
];

// Coordinates for major Indian cities.
const cityCoordinates = {
  "Mumbai": [72.8777, 19.0760],
  "Delhi": [77.2090, 28.6139],
  "Bangalore": [77.5946, 12.9716],
  "Hyderabad": [78.4867, 17.3850],
  "Ahmedabad": [72.5714, 23.0225],
  "Chennai": [80.2707, 13.0827],
  "Kolkata": [88.3639, 22.5726],
  "Surat": [72.8311, 21.1702],
  "Pune": [73.8567, 18.5204],
  "Jaipur": [75.7873, 26.9124],
  "Lucknow": [80.9462, 26.8467],
  "Kanpur": [80.3319, 26.4499],
  "Nagpur": [79.0882, 21.1458],
  "Indore": [75.8577, 22.7196],
  "Thane": [72.9781, 19.1970],
  "Bhopal": [77.4126, 23.2599],
  "Visakhapatnam": [83.2185, 17.6868],
  "Patna": [85.1376, 25.5941],
  "Vadodara": [73.1812, 22.3072],
  "Ghaziabad": [77.4538, 28.6692],
  "Ludhiana": [75.8573, 30.9010],
  "Agra": [78.0081, 27.1767],
  "Nashik": [73.7898, 19.9975],
  "Varanasi": [82.9739, 25.3176],
  "Srinagar": [74.7973, 34.0837],
  "Aurangabad": [75.3236, 19.8762],
  "Amritsar": [74.8723, 31.6340],
  "Navi Mumbai": [73.0297, 19.0330],
  "Allahabad": [81.8463, 25.4358],
  "Ranchi": [85.3096, 23.3441],
  "Coimbatore": [76.9558, 11.0168],
  "Jabalpur": [79.9414, 23.1815],
  "Gwalior": [78.1828, 26.2124],
  "Vijayawada": [80.6480, 16.5062],
  "Jodhpur": [73.0243, 26.2389],
  "Madurai": [78.1198, 9.9252],
  "Raipur": [81.6296, 21.2514],
  "Kota": [75.8391, 25.2138],
  "Chandigarh": [76.7794, 30.7333],
  "Guwahati": [91.7362, 26.1445],
  "Bada Bazaar": [88.3639, 22.5726],
  "Chandni Chowk": [77.2289, 28.6506],
  "Hathras": [78.0531, 27.6041],
  "Metiabruz": [88.2934, 22.5342],
  "Tank Road": [77.1950, 28.6500],
};

const stats = [
  { value: brandConfig.trust_stat_retailers, label: brandConfig.trust_stat_retailers_label, desc: 'Boutiques & shops across India', icon: '🏪' },
  { value: brandConfig.trust_stat_cities, label: brandConfig.trust_stat_cities_label, desc: 'Active delivery locations', icon: '📍' },
  { value: brandConfig.trust_stat_orders, label: brandConfig.trust_stat_orders_label, desc: 'Successfully fulfilled', icon: '📦' },
  { value: brandConfig.trust_stat_experience, label: brandConfig.trust_stat_experience_label, desc: 'In wholesale fashion supply', icon: '🏆' },
];

const TrustSection = () => {
  const { branches } = useBranches();
  const [hoveredBranch, setHoveredBranch] = useState(null);
  const navigate = useNavigate();

  // Map branches with collision avoidance so markers don't overlap
  const mapBranches = React.useMemo(() => {
    const placed = [];
    return branches.map(branch => {
      const origCoords = cityCoordinates[branch.city];
      if (!origCoords) return null;
      let coords = [...origCoords];
      
      let collision = true;
      let attempts = 0;
      while (collision && attempts < 20) {
        collision = false;
        for (const p of placed) {
          const dx = coords[0] - p[0];
          const dy = coords[1] - p[1];
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 0.8) { 
            collision = true;
            const angle = attempts * (Math.PI / 3); 
            const radius = 0.5 + (attempts * 0.15);
            coords = [
              origCoords[0] + Math.cos(angle) * radius,
              origCoords[1] + Math.sin(angle) * radius
            ];
            break;
          }
        }
        attempts++;
      }
      placed.push(coords);
      return { ...branch, coordinates: coords };
    }).filter(Boolean);
  }, [branches]);

  return (
    <section className={`${styles.section} section-pad`} aria-label="Nationwide Wholesale Distribution">
      <div className={styles.container}>
        
        {/* Left: Content & Stats */}
        <div className={styles.contentCol}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={styles.eyebrow}>Our Logistics Network</span>
            <h2 className={styles.title}>
              Your Most Trusted<br />
              <span className={styles.italicTitle}>Wholesale Partner</span>
            </h2>
            <p className={styles.desc}>
              Since {brandConfig.established_year}, we've built a robust distribution infrastructure across India. From our state-of-the-art warehouses to real-time order tracking, we ensure your fashion inventory arrives on time, every time.
            </p>
          </motion.div>

          {/* Luxury Stats Grid */}
          <div className={styles.statsGrid}>
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label} 
                className={styles.statCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className={styles.statHeader}>
                  <span className={styles.statIcon}>{stat.icon}</span>
                  <span className={styles.statValue}>{stat.value}</span>
                </div>
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statDesc}>{stat.desc}</span>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className={styles.ctaContainer}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Link to="/branches" className={styles.ctaPrimaryBtn}>
              Explore Network
            </Link>
            <Link to="/collections" className={styles.ctaSecondaryBtn}>
              Browse Catalog
            </Link>
          </motion.div>
        </div>

        {/* Right: Interactive India Map */}
        <div className={styles.mapCol}>
          
          {/* Branch Count Banner */}
          <motion.div 
            className={styles.mapStatsTop}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className={styles.pulseDot}></div>
            <span className={styles.branchCountText}>
              <strong>{branches.length}</strong> Active Branches Nationwide
            </span>
          </motion.div>

          <div className={styles.mapWrapper}>
            <ComposableMap
              projection="geoMercator"
              width={800}
              height={800}
              projectionConfig={{
                scale: 1400,
                center: [80, 22] // Center of India
              }}
              className={styles.svgMap}
            >
              <Geographies geography={geoData}>
                {({ geographies }) =>
                  geographies.map((geo, index) => {
                    const stateColor = mapColors[index % mapColors.length];
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        className={styles.geography}
                        style={{
                          default: { fill: stateColor, outline: "none" },
                          hover: { fill: "#CBD5E1", outline: "none", transition: "all 250ms" },
                          pressed: { fill: "#94A3B8", outline: "none" }
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {mapBranches.map((branch) => (
                <Marker 
                  key={branch.id} 
                  coordinates={branch.coordinates}
                >
                  <g 
                    transform="translate(-18, -36) scale(1.5)" 
                    className={styles.markerGroup}
                    onMouseEnter={() => setHoveredBranch(branch)}
                    onMouseLeave={() => setHoveredBranch(null)}
                    onClick={() => navigate(`/collections?branch=${branch.id}&branchName=${encodeURIComponent(branch.city)}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <path 
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
                      className={styles.markerPin} 
                    />
                    <circle cx="12" cy="9" r="2.5" fill="#fff" />
                  </g>
                  <text
                    textAnchor="middle"
                    y={-42}
                    style={{ fontFamily: "system-ui, sans-serif", fill: "#1e293b", fontSize: "12px", fontWeight: "700", pointerEvents: "none" }}
                  >
                    {branch.city}
                  </text>
                </Marker>
              ))}
            </ComposableMap>

            {/* Dynamic Tooltip */}
            <AnimatePresence>
              {hoveredBranch && (
                <motion.div 
                  className={styles.tooltip}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'absolute',
                    // Using some approximate positioning based on map hover is complex with ComposableMap purely via React state,
                    // so we place the tooltip fixed at top right or bottom of the map container, or we track mouse position.
                    // For enterprise feel, a floating card in the corner works exceptionally well.
                    bottom: '24px',
                    left: '24px',
                  }}
                >
                  <div className={styles.tooltipHeader}>
                    {hoveredBranch.isHeadOffice && (
                      <span className={styles.hoBadge}><Globe size={10} /> HEAD OFFICE</span>
                    )}
                    <h4 className={styles.tooltipCity}>{hoveredBranch.city}</h4>
                  </div>
                  <div className={styles.tooltipBody}>
                    <div className={styles.tooltipRow}>
                      <User size={12} className={styles.tooltipIcon} />
                      <span>{hoveredBranch.person}</span>
                    </div>
                    <div className={styles.tooltipRow}>
                      <Phone size={12} className={styles.tooltipIcon} />
                      <span>{hoveredBranch.phones[0]}</span>
                    </div>
                    <div className={styles.tooltipRow}>
                      <MapPin size={12} className={styles.tooltipIcon} />
                      <span className={styles.tooltipAddress}>{hoveredBranch.address}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </section>
  );
};

export default TrustSection;
